import { useEffect, useState } from 'react';
import { Modal } from './Overlay';
import { SelectField, TextField } from './Form';
import { InlineError } from './Feedback';
import { learningItems } from '../lib/endpoints';
import { errorMessage } from '../lib/api';
import type { LocalAccountDto, LocalItemDto } from '../lib/types';

const TYPE_OPTIONS = [
  { value: 'Service', label: 'Service' },
  { value: 'NonInventory', label: 'Non-inventory' },
];

/**
 * Creates a QuickBooks Item without leaving the invoice being reviewed — the gap that made a
 * document like a Business Central item number or a trading partner's own SKU with no match in
 * the item master a dead end, since nothing else in this app can create one. Purchase-only on
 * purpose: Service or NonInventory, one required expense account, nothing else — an Inventory or
 * two-sided item needs decisions (quantity tracking, an income account) a reviewer mid-invoice has
 * no business making, and the backend refuses anything wider anyway.
 */
export function CreateItemModal({
  open,
  onClose,
  defaultName,
  accounts,
  accountsLoading,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  defaultName: string;
  accounts: LocalAccountDto[];
  accountsLoading: boolean;
  onCreated: (item: LocalItemDto) => void;
}): JSX.Element | null {
  const [name, setName] = useState(defaultName);
  const [type, setType] = useState<'Service' | 'NonInventory'>('Service');
  const [expenseAccountId, setExpenseAccountId] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName(defaultName);
      setType('Service');
      setExpenseAccountId('');
      setDescription('');
      setError(null);
    }
  }, [open, defaultName]);

  if (!open) return null;

  const create = async () => {
    if (!name.trim()) {
      setError('A name is required.');
      return;
    }
    if (!expenseAccountId) {
      setError('An expense account is required — it decides where this item posts.');
      return;
    }

    setBusy(true);
    setError(null);

    try {
      const created = await learningItems.create({
        name: name.trim(),
        type,
        expenseAccountId,
        description: description.trim() || null,
      });
      onCreated(created);
      onClose();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Create a QuickBooks item"
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={() => void create()} disabled={busy}>
            {busy ? 'Creating…' : 'Create item'}
          </button>
        </>
      }
    >
      <div className="stack">
        <p className="field-hint">
          Only a Service or Non-inventory item can be created here — the kind a purchase line codes
          to. Nothing about it is guessed: you choose the account it posts to.
        </p>
        <TextField label="Name" value={name} onChange={setName} />
        <SelectField
          label="Type"
          value={type}
          onChange={(value) => setType(value as 'Service' | 'NonInventory')}
          options={TYPE_OPTIONS}
        />
        <SelectField
          label="Expense account"
          value={expenseAccountId}
          onChange={setExpenseAccountId}
          disabled={accountsLoading}
          options={[
            { value: '', label: accountsLoading ? 'Loading accounts…' : 'Choose an account' },
            ...accounts.map((account) => ({
              value: account.externalAccountId,
              label: account.accountNumber ? `${account.accountNumber} — ${account.name}` : account.name,
            })),
          ]}
        />
        <TextField
          label="Description"
          value={description}
          onChange={setDescription}
          hint="Shown on a purchase line for this item. Optional."
        />
        {error ? <InlineError message={error} /> : null}
      </div>
    </Modal>
  );
}
